/**
 * A platform feature flag and the tenants it is on for.
 *
 * Flags gate live platform behaviour in mesh — the NATS bus, the Postgres
 * entity repo, segment sharding, the entitlement graph — so the states here are
 * three, not two, and they are not symmetric: a global enable cannot be
 * overridden per tenant.
 */
export interface FeatureFlag {
  flagName: string;
  enabledTenantIds: string[];
  globalEnabled: boolean;
}

/**
 * How a flag resolves for one tenant.
 *
 * `global` is deliberately distinct from `tenant`: it means every tenant has
 * it, and it cannot be switched off for one of them.
 */
export type FlagState = 'global' | 'tenant' | 'off';

/**
 * Tenant ids arrive from two sources with different casing guarantees:
 * `enabled_tenant_ids` are Go `uuid.UUID`, marshalled lowercase-canonical,
 * while `GET /api/admin/tenants` returns a plain string that nothing validates
 * as a UUID. Comparing them raw silently reports "off" for a tenant whose id
 * is stored with different casing.
 */
function sameTenant(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function flagStateFor(flag: FeatureFlag, tenantId: string): FlagState {
  if (flag.globalEnabled) {
    return 'global';
  }
  return flag.enabledTenantIds.some((id) => sameTenant(id, tenantId))
    ? 'tenant'
    : 'off';
}

export function isEnabledForTenant(
  flag: FeatureFlag,
  tenantId: string
): boolean {
  return flagStateFor(flag, tenantId) !== 'off';
}

/**
 * Human-readable labels for the flags mesh currently knows about. The table
 * renders whatever the API returns rather than this list, so an unrecognised
 * flag still appears — this only supplies a friendlier name where we have one.
 */
export const FLAG_METADATA: Record<string, { label: string; blurb: string }> = {
  stream_bus_nats: {
    label: 'NATS stream bus',
    blurb: 'Route the event stream over NATS.',
  },
  distributed_leases: {
    label: 'Distributed leases',
    blurb: 'Coordinate workers with distributed leases.',
  },
  dual_publish_event_log: {
    label: 'Dual-publish event log',
    blurb: 'Also publish events to Redpanda.',
  },
  segment_sharding: {
    label: 'Segment sharding',
    blurb: 'Shard catalog segments across partitions.',
  },
  postgres_entity_repo: {
    label: 'Postgres entity repository',
    blurb: 'Back entities with Postgres instead of KV.',
  },
  cortex_sink_enabled: {
    label: 'Cortex sink',
    blurb: 'Send activity events to Cortex XSIAM as OCSF.',
  },
  entitlement_graph_enabled: {
    label: 'Entitlement graph',
    blurb: 'Build and serve the entitlement graph.',
  },
  advanced_account_analysis: {
    label: 'Advanced account analysis',
    blurb: 'Requires the entitlement graph; no-ops without it.',
  },
  siem_forwarding_enabled: {
    label: 'SIEM forwarding',
    blurb: 'Forward events to configured SIEM destinations.',
  },
  blast_radius_enabled: {
    label: 'Blast radius',
    blurb: 'Score account blast radius. Requires the entitlement graph.',
  },
};

export function flagLabel(flagName: string): string {
  return FLAG_METADATA[flagName]?.label ?? flagName;
}
